'use client'

import DragIndicator from '@mui/icons-material/DragIndicator'
import PushPin from '@mui/icons-material/PushPin'
import PushPinOutlined from '@mui/icons-material/PushPinOutlined'
import { Box, Chip, IconButton, Tooltip, Typography, alpha } from '@mui/material'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import type { DragEvent, KeyboardEvent, MouseEvent } from 'react'
import { useState } from 'react'

import type { NavLink } from '@core/layouts/types'

import usePinnedNavItems from '~/hooks/usePinnedNavItems'
import CanViewNavLink from '~/layouts/components/acl/CanViewNavLink'

import isNavItemActive from './isNavItemActive'

type Props = {
  item: NavLink
  /** When true, the row renders for the collapsed-group popover (no active ring guide). */
  inPopover?: boolean
  onNavigate?: () => void
  /** Render a drag handle and accept drag/keyboard reorder gestures. */
  reorderable?: boolean
  /** Position of this row inside its group's items array. Required when `reorderable`. */
  index?: number
  /** Number of sibling rows in this group. Required when `reorderable` (for keyboard bounds). */
  itemCount?: number
  onReorder?: (fromIndex: number, toIndex: number) => void
}

const DRAG_DATA_TYPE = 'application/x-synop-pinned-nav-index'

const VerticalNavChildRow = ({ item, inPopover, onNavigate, reorderable, index, itemCount, onReorder }: Props) => {
  const pathname = usePathname()
  const active = isNavItemActive(item, pathname)
  const isExternal = item.openInNewTab
  const t = useTranslations('layouts.navDrawer')
  const { isPinned, pin, unpin } = usePinnedNavItems()

  const canPin = !!item.path && !item.openInNewTab
  const pinned = canPin ? isPinned(item.path!) : false

  const togglePin = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (!item.path) return
    if (pinned) unpin(item.path)
    else pin(item.path)
  }

  // Drag-and-drop state for reordering. We highlight the row currently being
  // hovered over as a drop target so the user gets visual feedback for where
  // the dragged item will land.
  const [isDropTarget, setIsDropTarget] = useState(false)
  const dragEnabled = Boolean(reorderable && onReorder && typeof index === 'number')

  const onDragStart = (event: DragEvent<HTMLDivElement>) => {
    if (!dragEnabled || typeof index !== 'number') return
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData(DRAG_DATA_TYPE, String(index))
    // Also set text/plain so the OS shows a sensible drag preview tooltip.
    event.dataTransfer.setData('text/plain', item.title)
  }

  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!dragEnabled) return
    if (!event.dataTransfer.types.includes(DRAG_DATA_TYPE)) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    if (!isDropTarget) setIsDropTarget(true)
  }

  const onDragLeave = () => {
    if (isDropTarget) setIsDropTarget(false)
  }

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!dragEnabled || typeof index !== 'number' || !onReorder) return
    const raw = event.dataTransfer.getData(DRAG_DATA_TYPE)
    setIsDropTarget(false)
    if (!raw) return
    event.preventDefault()
    const from = Number.parseInt(raw, 10)
    if (Number.isNaN(from) || from === index) return
    onReorder(from, index)
  }

  const onHandleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!dragEnabled || typeof index !== 'number' || !onReorder || typeof itemCount !== 'number') return
    if (event.key === 'ArrowUp' && index > 0) {
      event.preventDefault()
      onReorder(index, index - 1)
    } else if (event.key === 'ArrowDown' && index < itemCount - 1) {
      event.preventDefault()
      onReorder(index, index + 1)
    }
  }

  // Active/hover/text styling lives on the outer row so the pin button picks
  // up the same hover background as the link. The link itself is just the
  // tappable target for the label/badge — keeping the `<button>` out of the
  // anchor's subtree (valid HTML, friendlier to assistive tech).
  return (
    <CanViewNavLink navLink={item}>
      <Box
        className={`nav-row${active ? ' active' : ''}${pinned ? ' pinned' : ''}${isDropTarget ? ' drop-target' : ''}`}
        draggable={dragEnabled}
        onDragStart={dragEnabled ? onDragStart : undefined}
        onDragOver={dragEnabled ? onDragOver : undefined}
        onDragLeave={dragEnabled ? onDragLeave : undefined}
        onDrop={dragEnabled ? onDrop : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: inPopover ? 42 : 36,
          pl: inPopover ? 2 : 1.5,
          pr: inPopover ? 1 : reorderable ? 0.5 : 1.5,
          borderRadius: 1,
          color: active ? (inPopover ? 'accent.main' : 'primary.contrastText') : 'text.primary',
          backgroundColor: active
            ? inPopover
              ? (theme) => alpha(theme.palette.accent.main, 0.08)
              : 'accent.main'
            : 'transparent',
          transition: 'background-color .12s ease-in-out, color .12s ease-in-out',
          '&:hover': {
            backgroundColor: active
              ? inPopover
                ? (theme) => alpha(theme.palette.accent.main, 0.12)
                : 'accent.dark'
              : 'action.hover'
          },
          // Pin affordance is hover/focus-only for both states. Pinned items
          // already live in their own group at the top of the sidebar, so a
          // persistent badge per row is redundant noise — surface the toggle
          // on demand instead.
          '& .nav-pin-button': {
            opacity: 0,
            transition: 'opacity .12s ease-in-out'
          },
          '&:hover .nav-pin-button, &:focus-within .nav-pin-button': {
            opacity: 1
          },
          '& .nav-drag-handle': {
            opacity: 0.4,
            transition: 'opacity .12s ease-in-out',
            cursor: 'grab'
          },
          '& .nav-drag-handle:active': { cursor: 'grabbing' },
          '&:hover .nav-drag-handle, &:focus-within .nav-drag-handle': { opacity: 1 },
          // Insertion preview while a sibling is being dragged over this row.
          '&.drop-target': {
            outline: '2px dashed',
            outlineColor: 'accent.main',
            outlineOffset: '-2px'
          }
        }}
      >
        <Box
          component={Link}
          href={item.path ?? '/'}
          {...(isExternal ? { target: '_blank', rel: 'noreferrer noopener' } : null)}
          onClick={() => onNavigate?.()}
          aria-current={active ? 'page' : undefined}
          data-cy={`nav-child-${item.title}`}
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            minWidth: 0,
            py: inPopover ? 0.5 : 0.75,
            fontSize: '0.875rem',
            lineHeight: '20px',
            letterSpacing: '0.17px',
            fontWeight: 400,
            textDecoration: 'none',
            color: 'inherit'
          }}
        >
          <Typography
            component="span"
            noWrap
            sx={{
              flex: 1,
              fontSize: 'inherit',
              fontWeight: 'inherit',
              color: 'inherit',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {item.title}
          </Typography>
          {item.badgeContent ? (
            <Chip
              size="small"
              label={item.badgeContent}
              color={item.badgeColor ?? 'primary'}
              sx={{
                height: 18,
                '& .MuiChip-label': {
                  px: 1,
                  fontSize: '0.625rem',
                  fontWeight: 700,
                  letterSpacing: '0.4px',
                  lineHeight: 1.4,
                  textTransform: 'uppercase'
                }
              }}
            />
          ) : null}
        </Box>
        {reorderable ? (
          <Tooltip title={t('reorderHandle')} placement="left">
            <IconButton
              className="nav-drag-handle"
              size="small"
              tabIndex={0}
              onKeyDown={onHandleKeyDown}
              data-cy={`nav-drag-${item.title}`}
              sx={{
                ml: 0.5,
                p: 0.25,
                color: active && !inPopover ? 'inherit' : 'text.secondary',
                '&:hover': { backgroundColor: 'transparent', color: 'inherit' }
              }}
            >
              <DragIndicator fontSize="inherit" />
            </IconButton>
          </Tooltip>
        ) : null}
        {canPin ? (
          // The Tooltip provides the accessible name via MUI's injected
          // aria-label, so we don't add a separate aria-label on the
          // IconButton (avoids screen readers announcing the same string
          // twice through both label and description).
          <Tooltip title={pinned ? t('unpinItem') : t('pinItem')} placement="right">
            <IconButton
              className="nav-pin-button"
              size="small"
              onClick={togglePin}
              aria-pressed={pinned}
              data-cy={`nav-pin-${item.title}`}
              sx={{
                ml: 0.5,
                p: 0.25,
                // Dark-grey for both states (filled = pinned, outlined =
                // unpinned) — the icon shape carries the toggle state, no
                // need for a brand-coloured shout. On the active blue row
                // we inherit the row's white text so the icon stays
                // legible against the background.
                color: active && !inPopover ? 'inherit' : 'text.primary',
                '&:hover': { backgroundColor: 'transparent' }
              }}
            >
              {pinned ? <PushPin fontSize="inherit" /> : <PushPinOutlined fontSize="inherit" />}
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
    </CanViewNavLink>
  )
}

export default VerticalNavChildRow
